import React, { useCallback, useEffect, useState } from 'react';
import { useResponsive } from 'hooks/useResponsive';
import { SiderLogo } from '../SiderLogo';
import { getMenuItems } from 'constants/MenuItems';
import { Menu, Image, Row, Col, Drawer } from 'antd';
import Sider from 'antd/es/layout/Sider';
import 'styles/sass/sider.scss';
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks';
import Link from 'antd/es/typography/Link';
import { BellFilled, WarningFilled, WhatsAppOutlined } from '@ant-design/icons';
import GetFrontendConfig from 'hooks/getFrontConfig';
import { whatsapp, whatsappLinkSupport } from 'constants/contactInfo';
import { doEmployeeMe, doGetMyContracts } from 'store/slices/employeeSlice';
import { SliceStatus } from 'constants/enums/slices';
import { ItemType, MenuItemGroupType } from 'antd/es/menu/interface';
import Contract from 'types/contract';
import { getActiveWithoutSchedules } from 'api/contract.api';
import { incidencePaths, usersPaths } from 'constants/routePaths';
import { Link as RRLink } from 'react-router';
import { AdminRole } from 'constants/enums/roles';
import { useIncidencePendingSummary } from 'hooks/incidencePendingSummary';

interface MainSiderProps {
  isCollapsed: boolean;
  setCollapsed: (isCollapsed: boolean) => void;
}

type MenuEntry = ItemType | MenuItemGroupType;

const renderIncidenceLabel = (
  label: React.ReactNode,
  indicatorDataCy?: string
): React.ReactNode => {
  const indicator = (
    <BellFilled
      className="main-menu-incidence-indicator"
      data-cy={indicatorDataCy}
    />
  );
  const renderContent = (labelContent: React.ReactNode) => (
    <span className="main-menu-incidence-label">
      <span>{labelContent}</span>
      {indicator}
    </span>
  );

  if (!React.isValidElement<{ children?: React.ReactNode }>(label)) {
    return renderContent(label);
  }

  return React.cloneElement(
    label,
    undefined,
    renderContent(label.props.children)
  );
};

const decorateIncidenceMenuItems = (
  items: MenuEntry[],
  hasPendingIncidenceRequests: boolean
): MenuEntry[] =>
  items.map((item) => {
    if (!item) {
      return item;
    }

    const shouldDecorateItem =
      hasPendingIncidenceRequests &&
      (item.key === incidencePaths.basePath ||
        item.key === 'employee-incidences' ||
        item.key ===
          `${incidencePaths.basePath}/${incidencePaths.authorizations}` ||
        item.key === `${incidencePaths.basePath}/${incidencePaths.approvals}`);

    const nextItem =
      shouldDecorateItem && 'label' in item
        ? {
            ...item,
            label: renderIncidenceLabel(
              item.label,
              item.key === incidencePaths.basePath ||
                item.key === 'employee-incidences'
                ? 'incidence-menu-pending-indicator'
                : 'incidence-submenu-pending-indicator'
            ),
          }
        : item;

    if (!('children' in nextItem) || !nextItem.children) {
      return nextItem;
    }

    return {
      ...nextItem,
      children: decorateIncidenceMenuItems(
        nextItem.children as MenuEntry[],
        hasPendingIncidenceRequests
      ),
    };
  });

const MainSider: React.FC<MainSiderProps> = ({
  isCollapsed,
  setCollapsed,
  ...props
}) => {
  const dispatch = useAppDispatch();
  const { mobileOnly } = useResponsive();
  const { user } = useAppSelector((state) => state.auth);
  const { employee, employeeSliceStatus, ownContracts, ownContractsStatus } =
    useAppSelector((state) => state.employee);
  const frontendConfig = GetFrontendConfig();
  const [menuItems, setMenuItems] = useState<
    ItemType[] | MenuItemGroupType[]
  >();
  const [activeContractsWithoutSchedules, setActiveContractsWithoutSchedules] =
    useState<Contract[]>([]);
  const effectiveIsSupervisor =
    employee._id === user?._id
      ? Boolean(employee.isSupervisor)
      : Boolean(user?.isSupervisor);
  const isIncidencesModuleEnabled =
    frontendConfig?.isIncidencesModuleEnabled !== 'false';
  const isIncidenceAuthorizationsEnabled =
    frontendConfig?.isIncidenceAuthorizationsEnabled !== 'false';
  const canViewIncidencePendingIndicator = Boolean(
    user &&
    isIncidencesModuleEnabled &&
    ((user.role === AdminRole && isIncidenceAuthorizationsEnabled) ||
      effectiveIsSupervisor)
  );
  const { hasPending: hasPendingIncidenceRequests } =
    useIncidencePendingSummary({
      enabled: canViewIncidencePendingIndicator,
    });

  const searchActiveContractsWithouSchedules = useCallback(async () => {
    try {
      const response = await getActiveWithoutSchedules();
      setActiveContractsWithoutSchedules(response.data ?? []);
    } catch {
      setActiveContractsWithoutSchedules([]);
    }
  }, []);

  useEffect(() => {
    if (user?.role === AdminRole) {
      void searchActiveContractsWithouSchedules();
    }
  }, [searchActiveContractsWithouSchedules, user]);

  useEffect(() => {
    if (
      user &&
      (employeeSliceStatus === SliceStatus.Empty ||
        (employeeSliceStatus !== SliceStatus.Updating &&
          employeeSliceStatus !== SliceStatus.Error &&
          employee._id !== user._id))
    ) {
      dispatch(doEmployeeMe());
    }
  }, [dispatch, employee._id, employeeSliceStatus, user]);

  const [current, setCurrent] = useState('');
  const currentPath = location.pathname;

  const menuItemsSetter = useCallback(() => {
    if (user && frontendConfig) {
      const items = getMenuItems(
        user.role,
        effectiveIsSupervisor,
        ownContracts,
        frontendConfig.isSchedulesModuleEnabled,
        frontendConfig.isIncidencesModuleEnabled,
        frontendConfig.isMyIncidenceRequestsEnabled,
        frontendConfig.isIncidenceAuthorizationsEnabled
      );
      if (items) {
        let finalItems = [...items];
        if (activeContractsWithoutSchedules.length > 0) {
          finalItems = finalItems.map((item) => {
            if (item?.key === usersPaths.basePath) {
              return {
                ...item,
                label: (
                  <Row>
                    <Col xs={22}>
                      <RRLink to={usersPaths.basePath}>Empleados</RRLink>
                    </Col>
                    <Col>
                      <WarningFilled className="warning-icon" />
                    </Col>
                  </Row>
                ),
              };
            }
            return item;
          });
        }
        finalItems = decorateIncidenceMenuItems(
          finalItems,
          hasPendingIncidenceRequests
        );
        setMenuItems(finalItems);
      }
    }
  }, [
    effectiveIsSupervisor,
    ownContracts,
    setMenuItems,
    user,
    frontendConfig,
    activeContractsWithoutSchedules,
    hasPendingIncidenceRequests,
  ]);

  useEffect(() => {
    menuItemsSetter();
  }, [menuItemsSetter]);

  useEffect(() => {
    if (user) {
      if (ownContractsStatus === SliceStatus.Empty) {
        dispatch(doGetMyContracts(user._id));
      }
    }
  }, [dispatch, ownContractsStatus, user]);

  useEffect(() => {
    const menuKeys: string[] = [];
    if (menuItems) {
      menuItems.forEach((item: ItemType | MenuItemGroupType) => {
        menuKeys.push(item?.key as string);
        if ((item as MenuItemGroupType).children) {
          const children = (item as MenuItemGroupType).children;
          children?.forEach((childrenItem) => {
            menuKeys.push(childrenItem?.key as string);
          });
        }
      });
      if (menuKeys.includes(currentPath)) {
        setCurrent(currentPath);
      }
    }
  }, [currentPath, menuItems]);

  const siderContent = (
    <div className="sider-flex">
      <div>
        <SiderLogo />
        {user && ownContracts && (
          <Menu
            items={menuItems}
            mode="inline"
            selectedKeys={[current]}
            className="main-menu"
            onClick={() => {
              if (mobileOnly) {
                setCollapsed(true);
              }
            }}
          />
        )}
      </div>
      <div className="sider-footer">
        {frontendConfig && frontendConfig.siderLogoUrl && (
          <div className="logo-inst">
            <Image
              width={130}
              src={frontendConfig.siderLogoUrl}
              alt="logo"
              preview={false}
            />
          </div>
        )}
        <Link
          className="support-link"
          href={whatsappLinkSupport}
          target="_blank"
        >
          Soporte Técnico <WhatsAppOutlined /> {whatsapp}
        </Link>
        <div className="app-version" data-cy="app-version">
          Versión: {import.meta.env.VITE_IMAGE_TAG || 'desarrollo'}
        </div>
      </div>
    </div>
  );

  if (mobileOnly) {
    return (
      <Drawer
        placement="left"
        closable={false}
        onClose={() => setCollapsed(true)}
        open={!isCollapsed}
        width={260}
        bodyStyle={{ padding: 0 }}
      >
        {siderContent}
      </Drawer>
    );
  }

  return (
    <>
      <Sider
        className="sider-wrapper"
        trigger={null}
        collapsed={false}
        collapsible={false}
        width={260}
        {...props}
      >
        {siderContent}
      </Sider>
    </>
  );
};

export default MainSider;
